import type { CrowdexAgentInteraction, CrowdexUIMessage } from '@crowdex/agent-contracts';
import { Message, MessageContent } from '@crowdex/ui/ai/message';
import { type ReactNode, useCallback, useEffect } from 'react';
import { DesktopMessageList } from '@/components/desktop-message-list';
import { TurnWorking } from '@/components/desktop-message-part/turn-steps';
import { ToolApprovalPanel } from '@/components/tool-approval-panel';
import { onStreamedEvent } from '@/features/notifications/notification-feed';
import { useInterruptedActionResume } from '@/hooks/use-interrupted-action-resume';
import { AgentElicitationContext } from '@/lib/conversations/message-part/agent-elicitation-context';
import { InterruptedActionContext } from '@/lib/conversations/message-part/interrupted-action-context';
import { recordQuestionAnswer } from '@/lib/conversations/message-part/question-answer';
import type { AnswerToolApproval } from '@/lib/conversations/message-part/tool-approval';
import { hasPendingToolApproval } from '@/lib/conversations/message-part/tool-approval';

/**
 * Provides the shared "resume / answer an interrupted item" actions to every
 * conversation surface (main pane, split pane, side chat) so the wiring lives in
 * one place. Each surface only supplies its own canonical `onSend` — the main
 * pane routes through steer/queue while a turn runs; the simpler panes send
 * directly — and the interruption/grace logic is identical everywhere.
 */
export function ConversationActionsProvider({
  messages,
  ready,
  busy,
  onSend,
  respondToInteraction,
  setMessages,
  children,
}: {
  messages: readonly CrowdexUIMessage[];
  ready: boolean;
  busy: boolean;
  /** `answers`: the agent's question (its tool call) the text answers, while its turn runs. */
  onSend: (text: string, answers?: string) => void;
  respondToInteraction?: (interaction: CrowdexAgentInteraction) => Promise<void>;
  setMessages: (updater: (current: CrowdexUIMessage[]) => CrowdexUIMessage[]) => void;
  children: ReactNode;
}) {
  const saveQuestionAnswer = useCallback(
    (toolCallId: string, answer: string) => {
      setMessages((current) => recordQuestionAnswer(current, toolCallId, answer));
    },
    [setMessages],
  );
  // A question answered elsewhere (Needs you, another device) is answered here too.
  useEffect(
    () =>
      onStreamedEvent((event) => {
        const { change, request, answer } = event.fields;
        if (
          event.type === 'crowdex/conversation.changed' &&
          change === 'answered' &&
          typeof request === 'string' &&
          typeof answer === 'string'
        )
          saveQuestionAnswer(request, answer);
      }),
    [saveQuestionAnswer],
  );
  const value = useInterruptedActionResume({
    messages,
    ready,
    hasPendingToolApproval: hasPendingToolApproval(messages),
    busy,
    sendMessage: (message) => onSend(message.text, message.answers),
    recordQuestionAnswer: saveQuestionAnswer,
  });
  return (
    <AgentElicitationContext.Provider
      value={
        respondToInteraction === undefined
          ? null
          : {
              onAnswer: (requestId, outcome) =>
                void respondToInteraction({ type: 'elicitation', requestId, outcome }),
            }
      }
    >
      <InterruptedActionContext.Provider value={value}>
        {children}
      </InterruptedActionContext.Provider>
    </AgentElicitationContext.Provider>
  );
}

/**
 * Renders the message list plus the "working…" status bubble shown while the
 * agent is thinking. Shared by the side chat and split conversation panes,
 * which differ only in the class applied to the working-status message.
 */
export function ConversationMessageStream({
  messages,
  status,
  messageClassName,
}: {
  messages: CrowdexUIMessage[];
  status: string;
  messageClassName?: string;
}) {
  return (
    <>
      <DesktopMessageList
        messages={messages}
        streamingMessageId={
          status === 'streaming' && messages.at(-1)?.role === 'assistant'
            ? messages.at(-1)?.id
            : undefined
        }
      />
      {(status === 'submitted' ||
        (status === 'streaming' && messages.at(-1)?.role !== 'assistant')) && (
        <Message className={messageClassName} from="assistant">
          <MessageContent>
            <TurnWorking />
          </MessageContent>
        </Message>
      )}
    </>
  );
}

/**
 * Renders native AI SDK tool approvals. Shared verbatim by the side chat and
 * split conversation panes.
 */
export function ConversationConnectionPrompts({
  messages,
  onToolApproval,
}: {
  messages: CrowdexUIMessage[];
  onToolApproval: AnswerToolApproval;
}) {
  return <ToolApprovalPanel messages={messages} onAnswer={onToolApproval} />;
}
